import React, { useEffect, useMemo, useRef, useState } from 'react';
import AssignmentFormCanvas from './AssignmentFormCanvas';
import AssignmentAnswerKeyPanel from './AssignmentAnswerKeyPanel';
import './AssignmentFormCanvas.css';
import { isPointsForm, scoreSubmission } from '../../utils/formScoring';
import { stripHtmlToText } from '../../utils/richText';
import { FORM_RELATIONSHIP_TYPE_LABELS } from '../../constants/formRelationships';
import {
  resolveAssignmentAnswerVisibility,
  buildScoringFeedbackMap,
  resolveSubmissionPolicy,
  getAttemptLimitDisplay,
  getSubmitActionHelp
} from '../../utils/formAssignmentMetadata';
import {
  SUBMISSION_ACTIONS,
  validateRequiredForSubmit,
  canSubmitDraft,
  isSubmissionLockedForStudent,
  resolveSubmissionAttemptDisplay,
  resolveGradingBadgeLabel,
  GRADING_STATUS,
  extractUserAnswers
} from '../../utils/formSubmissionLifecycle';
import { getScoringBreakdown } from '../../utils/submissionAnswers';
import AssignmentCollaborationPanel from './AssignmentCollaborationPanel';
import './AssignmentCollaborationPanel.css';
import './FormAssignmentViewer.css';

function applyManualPointsToAnswers(form, answers, manualByQuestionId) {
  const userAnswers = extractUserAnswers(answers);
  const scored = scoreSubmission(form.type, form.questions, userAnswers);
  if (!scored) return answers;

  const breakdown = (scored.breakdown || []).map((row) => {
    const override = manualByQuestionId[row.questionId];
    if (override == null || override === '') return row;
    const earned = Math.max(0, Number(override) || 0);
    return { ...row, earned, manualOverride: true };
  });

  const total = breakdown.reduce((sum, row) => sum + (Number(row.earned) || 0), 0);
  const maxScore = breakdown.reduce((sum, row) => sum + (Number(row.max) || 0), 0);
  const rounded = Math.round(total * 100) / 100;
  const roundedMax = Math.round(maxScore * 100) / 100;

  return {
    ...userAnswers,
    score: rounded,
    maxScore: roundedMax,
    _scoring: {
      score: rounded,
      maxScore: roundedMax,
      breakdown
    }
  };
}

const FormAssignmentViewer = ({
  form,
  submissions = [],
  selectedSubmissionId,
  onSelectSubmission,
  formData,
  onFormDataChange,
  originalFormData,
  isSubmitting = false,
  answersReadOnly = false,
  canCreateSubmission = false,
  canDeleteSubmission = false,
  canViewQuestionAnswers = false,
  canUpdatePoints = false,
  assignmentRelationshipId = null,
  collaborationCapabilities = null,
  currentUserId = null,
  fetchWithAuth,
  assignmentMetadata = null,
  formMetadata = null,
  hasSubmission = false,
  onSave,
  onSubmit,
  onApplyPoints,
  onCancel,
  onDelete,
  onNewSubmission,
  onClose,
  onPendingFileRegister,
  onPendingFileRelease,
  documentUrlById = {},
  pendingFileBlobs = {},
  selectedSubmission = null
}) => {
  const [manualPoints, setManualPoints] = useState({});
  const canvasRef = useRef(null);

  const pointsForm = isPointsForm(form?.type);
  const scoringBreakdown = getScoringBreakdown(formData);
  const answerVisibility = resolveAssignmentAnswerVisibility({
    assignmentMetadata,
    formMetadata,
    hasSubmission,
    gradingStatus: selectedSubmission?.grading_status,
    canViewQuestionAnswers,
    canUpdatePoints
  });
  const scoringFeedback =
    answerVisibility.showQuestionScores && hasSubmission
      ? buildScoringFeedbackMap(formData, {
          revealCorrectness: answerVisibility.showWrongFeedback
        })
      : null;

  useEffect(() => {
    const next = {};
    scoringBreakdown.forEach((row) => {
      if (row?.questionId != null) {
        next[row.questionId] = row.earned != null ? String(row.earned) : '';
      }
    });
    setManualPoints(next);
  }, [selectedSubmissionId, form?.id]);

  const hasAnswerChanges = useMemo(() => {
    const current = formData || {};
    const original = originalFormData || {};
    const curKeys = Object.keys(extractUserAnswers(current));
    const origKeys = Object.keys(extractUserAnswers(original));
    if (curKeys.length !== origKeys.length) return true;
    for (const key of curKeys) {
      if (current[key] !== original[key]) return true;
    }
    return false;
  }, [formData, originalFormData]);

  const hasPointsChanges = useMemo(() => {
    if (!canUpdatePoints || !pointsForm) return false;
    for (const row of scoringBreakdown) {
      const draft = manualPoints[row.questionId];
      if (draft != null && String(draft) !== String(row.earned ?? '')) return true;
    }
    return false;
  }, [manualPoints, scoringBreakdown, canUpdatePoints, pointsForm]);

  const handleSave = () => {
    if (!onSave || !hasAnswerChanges) return;
    onSave(formData, SUBMISSION_ACTIONS.SAVE);
  };

  const handleApplyPoints = () => {
    if (!onApplyPoints || !hasPointsChanges) return;
    const merged = applyManualPointsToAnswers(form, formData, manualPoints);
    onApplyPoints(merged);
  };

  const handleSubmit = () => {
    if (!onSubmit) return;
    const validation = validateRequiredForSubmit(form, formData, pendingFileBlobs);
    if (!validation.ok) {
      if (canvasRef.current) canvasRef.current.validate();
      return;
    }
    if (canvasRef.current && !canvasRef.current.validate()) return;
    onSubmit(formData);
  };

  const isDraft = canSubmitDraft(selectedSubmission);
  const isLocked = isSubmissionLockedForStudent(selectedSubmission);
  const submitReady = validateRequiredForSubmit(form, formData, pendingFileBlobs).ok;
  const submissionPolicy = useMemo(
    () =>
      resolveSubmissionPolicy({
        assignmentMetadata,
        formMetadata
      }),
    [assignmentMetadata, formMetadata]
  );
  const attemptLimit = useMemo(
    () => getAttemptLimitDisplay(submissionPolicy, submissions),
    [submissionPolicy, submissions]
  );
  const submitHelpText = useMemo(
    () => getSubmitActionHelp(submissionPolicy),
    [submissionPolicy]
  );
  const showDraftActions = !answersReadOnly && isDraft && (!!onSave || !!onSubmit);
  const showCancel = showDraftActions && hasAnswerChanges;
  const showSubmit = showDraftActions && !!onSubmit;
  const showApplyPoints =
    canUpdatePoints && pointsForm && hasPointsChanges && scoringBreakdown.length > 0;
  const showActions =
    showDraftActions || (canDeleteSubmission && selectedSubmissionId) || (isLocked && !showDraftActions);
  const gradingLabel = resolveGradingBadgeLabel(selectedSubmission);
  const gradingBadgeHint =
    selectedSubmission?.grading_status === GRADING_STATUS.NOT_REQUIRED &&
    canSubmitDraft(selectedSubmission);

  const totalScoreDisplay = (() => {
    if (!answerVisibility.showQuestionScores) return null;
    const s = formData?.score;
    const m = formData?.maxScore;
    if (s != null && m != null) return `${s} / ${m} points`;
    return null;
  })();

  return (
    <div className="profile-assignment-viewer">
      <header className="profile-assignment-viewer__header">
        {onClose ? (
          <button
            type="button"
            className="profile-assignment-viewer__close"
            onClick={onClose}
            aria-label="Close assignment"
          >
            ×
          </button>
        ) : null}
        <div className="profile-assignment-viewer__header-main">
          <span className="profile-assignment-viewer__eyebrow">
            {FORM_RELATIONSHIP_TYPE_LABELS.assignment}
          </span>
          <h2 className="profile-assignment-viewer__title">{form?.title}</h2>
          {totalScoreDisplay ? (
            <span className="profile-assignment-viewer__score">{totalScoreDisplay}</span>
          ) : null}
          {gradingLabel ? (
            <span
              className={`profile-assignment-viewer__grading-badge${
                gradingBadgeHint ? ' profile-assignment-viewer__grading-badge--hint' : ''
              }`}
            >
              {gradingLabel}
            </span>
          ) : null}
        </div>
        <div className="profile-assignment-viewer__header-actions">
          <AssignmentCollaborationPanel
            submissionId={selectedSubmissionId}
            submissionCount={submissions.length}
            assignmentRelationshipId={assignmentRelationshipId}
            capabilities={collaborationCapabilities}
            currentUserId={currentUserId}
            fetchWithAuth={fetchWithAuth}
          />
        </div>
      </header>

      <div className="profile-assignment-viewer__body">
        <aside className="profile-assignment-viewer__submissions">
          <div className="profile-assignment-viewer__submissions-head">
            <div className="profile-assignment-viewer__submissions-head-text">
              <span>Attempts</span>
              <span className="profile-assignment-viewer__attempt-limit">{attemptLimit.short}</span>
            </div>
            {canCreateSubmission ? (
              <button type="button" className="profile-assignment-viewer__new" onClick={onNewSubmission}>
                + New
              </button>
            ) : null}
          </div>
          <p className="profile-assignment-viewer__attempt-limit-detail">{attemptLimit.detail}</p>
          {submissions.length === 0 ? (
            <p className="profile-assignment-viewer__empty">No attempts yet.</p>
          ) : (
            <ul className="profile-assignment-viewer__submission-list">
              {submissions.map((sub, index) => {
                const display = resolveSubmissionAttemptDisplay(sub, index, submissions.length);
                return (
                  <li key={sub.id}>
                    <button
                      type="button"
                      className={`profile-assignment-viewer__submission-item${
                        String(sub.id) === String(selectedSubmissionId) ? ' is-active' : ''
                      }`}
                      onClick={() => onSelectSubmission?.(sub.id)}
                    >
                      <span className="profile-assignment-viewer__attempt-content">
                        {display.attemptNumber != null ? (
                          <span className="profile-assignment-viewer__attempt-index">
                            {display.attemptNumber}
                          </span>
                        ) : null}
                        {display.when ? (
                          <span className="profile-assignment-viewer__attempt-when">{display.when}</span>
                        ) : null}
                        <span
                          className={`profile-assignment-viewer__status-badge profile-assignment-viewer__status-badge--${display.statusKey}`}
                        >
                          {display.statusLabel}
                        </span>
                        {answerVisibility.showQuestionScores && display.scoreLabel ? (
                          <span className="profile-assignment-viewer__attempt-score">{display.scoreLabel}</span>
                        ) : null}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        <main className="profile-assignment-viewer__main profile-assignment-viewer__main--canvas">
          {!selectedSubmissionId && submissions.length === 0 && !canCreateSubmission ? (
            <p className="profile-assignment-viewer__empty">You do not have permission to start a submission.</p>
          ) : (
            <>
              <AssignmentFormCanvas
                ref={canvasRef}
                form={form}
                formData={formData}
                onFormDataChange={answersReadOnly ? undefined : onFormDataChange}
                readOnly={answersReadOnly}
                isSubmitting={isSubmitting}
                hideTitleInHero
                scoringFeedback={scoringFeedback}
                onPendingFileRegister={onPendingFileRegister}
                onPendingFileRelease={onPendingFileRelease}
                documentUrlById={documentUrlById}
              />
              {showActions ? (
                <div className="profile-assignment-viewer__actions profile-assignment-viewer__actions--canvas">
                  {showDraftActions ? (
                    <div className="profile-assignment-viewer__action-cards">
                      <div className="profile-assignment-viewer__action-cards-main">
                        {onSave ? (
                          <button
                            type="button"
                            className="profile-assignment-viewer__action-card profile-assignment-viewer__action-card--save"
                            disabled={isSubmitting || !hasAnswerChanges}
                            onClick={handleSave}
                            aria-label="Save your progress as a draft"
                          >
                            <span className="profile-assignment-viewer__action-card-text">
                              <span className="profile-assignment-viewer__action-card-title">
                                Save your progress
                              </span>
                              <span className="profile-assignment-viewer__action-card-desc">
                                Your answers are not saved automatically. Save this draft so you
                                can return and continue later.
                              </span>
                            </span>
                            <span className="profile-assignment-viewer__action-card-cta">
                              {isSubmitting ? 'Saving…' : 'Save draft'}
                            </span>
                          </button>
                        ) : null}
                        {onSubmit ? (
                          <button
                            type="button"
                            className="profile-assignment-viewer__action-card profile-assignment-viewer__action-card--primary"
                            disabled={isSubmitting || !submitReady}
                            onClick={handleSubmit}
                            aria-label="Submit for grading"
                          >
                            <span className="profile-assignment-viewer__action-card-text">
                              <span className="profile-assignment-viewer__action-card-title">
                                Submit for grading
                              </span>
                              <span className="profile-assignment-viewer__action-card-desc">
                                {submitHelpText}
                              </span>
                              {!submitReady ? (
                                <span className="profile-assignment-viewer__action-card-note">
                                  Complete all required questions before you can submit.
                                </span>
                              ) : null}
                            </span>
                            <span className="profile-assignment-viewer__action-card-cta profile-assignment-viewer__action-card-cta--primary">
                              {isSubmitting ? 'Submitting…' : 'Submit'}
                            </span>
                          </button>
                        ) : null}
                      </div>
                      {showCancel ? (
                        <button
                          type="button"
                          className="profile-assignment-viewer__action-card profile-assignment-viewer__action-card--discard"
                          disabled={isSubmitting}
                          onClick={onCancel}
                          aria-label="Discard changes and reset to last saved version"
                        >
                          <span className="profile-assignment-viewer__action-card-x" aria-hidden>
                            ×
                          </span>
                          <span className="profile-assignment-viewer__action-card-text">
                            <span className="profile-assignment-viewer__action-card-title">
                              Discard changes
                            </span>
                            <span className="profile-assignment-viewer__action-card-desc">
                              Resets to your last saved version.
                            </span>
                          </span>
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                  {isLocked && !showDraftActions ? (
                    <span className="profile-assignment-viewer__hint">Submitted — view only</span>
                  ) : null}
                  {canDeleteSubmission && selectedSubmissionId ? (
                    <div className="profile-assignment-viewer__action-footer">
                      <button
                        type="button"
                        className="profile-assignment-viewer__btn profile-assignment-viewer__btn--danger"
                        disabled={isSubmitting}
                        onClick={onDelete}
                      >
                        Delete attempt
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        </main>

        {(answerVisibility.showAnswerKey || (canUpdatePoints && pointsForm)) && (
          <aside className="profile-assignment-viewer__sidebar">
            {answerVisibility.showAnswerKey ? (
              <section className="profile-assignment-viewer__panel">
                <h3 className="profile-assignment-viewer__panel-title">Answer key</h3>
                <p className="profile-assignment-viewer__panel-desc">
                  Expected responses set by the form author.
                </p>
                <AssignmentAnswerKeyPanel form={form} />
              </section>
            ) : null}

            {canUpdatePoints && pointsForm ? (
              <section className="profile-assignment-viewer__panel">
                <h3 className="profile-assignment-viewer__panel-title">Points</h3>
                {scoringBreakdown.length === 0 ? (
                  <p className="profile-assignment-viewer__hint">Save a submission to score points.</p>
                ) : (
                  <>
                    <ul className="profile-assignment-viewer__points-list">
                      {scoringBreakdown.map((row) => (
                        <li key={row.questionId} className="profile-assignment-viewer__points-row">
                          <span className="profile-assignment-viewer__points-label">
                            {row.label || stripHtmlToText(row.label) || 'Question'}
                          </span>
                          <div className="profile-assignment-viewer__points-input-wrap">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="profile-assignment-viewer__points-input"
                              value={manualPoints[row.questionId] ?? ''}
                              disabled={isSubmitting}
                              onChange={(e) =>
                                setManualPoints((prev) => ({
                                  ...prev,
                                  [row.questionId]: e.target.value
                                }))
                              }
                            />
                            <span className="profile-assignment-viewer__points-max">/ {row.max}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                    {showApplyPoints ? (
                      <div className="profile-assignment-viewer__points-actions">
                        <button
                          type="button"
                          className="profile-assignment-viewer__btn profile-assignment-viewer__btn--primary"
                          disabled={isSubmitting}
                          onClick={handleApplyPoints}
                        >
                          {isSubmitting ? 'Applying…' : 'Apply points'}
                        </button>
                      </div>
                    ) : null}
                  </>
                )}
              </section>
            ) : null}
          </aside>
        )}
      </div>
    </div>
  );
};

export default FormAssignmentViewer;
export { applyManualPointsToAnswers, extractUserAnswers };
